import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';

// Lazy loaded pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardLayout = lazy(() => import('./pages/DashboardLayout'));
const DashboardHome = lazy(() => import('./pages/DashboardHome'));
const ConversationsPage = lazy(() => import('./pages/ConversationsPage'));
const VisitorsPage = lazy(() => import('./pages/VisitorsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const TeamPerformancePage = lazy(() => import('./pages/TeamPerformancePage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'));
const GeneralSettings = lazy(() => import('./pages/settings/GeneralSettings'));
const WidgetSettings = lazy(() => import('./pages/settings/WidgetSettings'));
const AgentSettings = lazy(() => import('./pages/settings/AgentSettings'));
const CannedResponses = lazy(() => import('./pages/settings/CannedResponses'));
const TagsSettings = lazy(() => import('./pages/settings/TagsSettings'));
const NotificationSettings = lazy(() => import('./pages/settings/NotificationSettings'));
const BillingSettings = lazy(() => import('./pages/settings/BillingSettings'));
const SecuritySettings = lazy(() => import('./pages/settings/SecuritySettings'));
const WorkingHoursSettings = lazy(() => import('./pages/settings/WorkingHoursSettings'));
const DomainSettings = lazy(() => import('./pages/settings/DomainSettings'));
const HolidaySettings = lazy(() => import('./pages/settings/HolidaySettings'));
const EmailSettings = lazy(() => import('./pages/settings/EmailSettings'));
const RoutingSettings = lazy(() => import('./pages/settings/RoutingSettings'));
const AISettings = lazy(() => import('./pages/settings/AISettings'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const WidgetChat = lazy(() => import('./pages/WidgetChat'));
const AgentLoginPage = lazy(() => import('./pages/AgentLoginPage'));
// V4 pages
const PageVisitAnalyticsPage = lazy(() => import('./pages/PageVisitAnalyticsPage'));
const FingerprintDashboardPage = lazy(() => import('./pages/FingerprintDashboardPage'));
const FingerprintDetailPage = lazy(() => import('./pages/FingerprintDetailPage'));
const WebsiteManagementPage = lazy(() => import('./pages/WebsiteManagementPage'));
const SessionRecordingsPage = lazy(() => import('./pages/SessionRecordingsPage'));
const SessionReplayPage = lazy(() => import('./pages/SessionReplayPage'));
const ClickHeatmapsPage = lazy(() => import('./pages/ClickHeatmapsPage'));
const BehaviorAnalyticsPage = lazy(() => import('./pages/BehaviorAnalyticsPage'));
const NavigationFlowPage = lazy(() => import('./pages/NavigationFlowPage'));
const AgentScoringDashboardPage = lazy(() => import('./pages/AgentScoringDashboardPage'));
const AgentActivityPage = lazy(() => import('./pages/AgentActivityPage'));
const BusinessDetailsPage = lazy(() => import('./pages/BusinessDetailsPage'));
const PopupManagerPage = lazy(() => import('./pages/PopupManagerPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
  </div>
);

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, isAuthenticated } = useAuthStore();
  if (!token && !isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Admin Route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user?.is_superadmin && user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <Toaster position="top-right" toastOptions={{
        duration: 4000,
        style: { background: '#1e293b', color: '#f8fafc', borderRadius: '12px' }
      }} />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/agent/login" element={<AgentLoginPage />} />
          <Route path="/widget" element={<WidgetChat />} />

          {/* Dashboard routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashboardHome />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route path="visitors" element={<VisitorsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="team" element={<TeamPerformancePage />} />
            <Route path="knowledge" element={<KnowledgeBasePage />} />
            <Route path="help" element={<HelpCenterPage />} />

            {/* V4 Analytics */}
            <Route path="page-visits" element={<PageVisitAnalyticsPage />} />
            <Route path="fingerprints" element={<FingerprintDashboardPage />} />
            <Route path="fingerprints/:id" element={<FingerprintDetailPage />} />
            <Route path="websites" element={<WebsiteManagementPage />} />
            <Route path="recordings" element={<SessionRecordingsPage />} />
            <Route path="recordings/:id" element={<SessionReplayPage />} />
            <Route path="heatmaps" element={<ClickHeatmapsPage />} />
            <Route path="behavior" element={<BehaviorAnalyticsPage />} />
            <Route path="navigation" element={<NavigationFlowPage />} />
            <Route path="scoring" element={<AgentScoringDashboardPage />} />
            <Route path="scoring/activity" element={<AgentActivityPage />} />
            <Route path="business-details" element={<BusinessDetailsPage />} />
            <Route path="popups" element={<PopupManagerPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />

            {/* Settings */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<GeneralSettings />} />
              <Route path="widget" element={<WidgetSettings />} />
              <Route path="agents" element={<AgentSettings />} />
              <Route path="canned-responses" element={<CannedResponses />} />
              <Route path="tags" element={<TagsSettings />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="billing" element={<BillingSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="working-hours" element={<WorkingHoursSettings />} />
              <Route path="holidays" element={<HolidaySettings />} />
              <Route path="domains" element={<DomainSettings />} />
              <Route path="email" element={<EmailSettings />} />
              <Route path="routing" element={<RoutingSettings />} />
              <Route path="ai" element={<AISettings />} />
            </Route>

            {/* Admin */}
            <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="audit-logs" element={<AdminRoute><AuditLogsPage /></AdminRoute>} />
            <Route path="super-admin" element={<AdminRoute><SuperAdminPage /></AdminRoute>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
